import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import {
  Stack,
  Button,
  Text,
  TextInput,
  Loader,
  SegmentedControl,
  Alert,
  Group,
  Select,
} from "@mantine/core";
import { IconAlertTriangle, IconTrash } from "@tabler/icons-react";

import { Bob } from "../../stores/Bob";
import TwoFactorInput from "../details/twoFactorInput";
import { BOB_MODALS } from "../../helpers/constants";

const EditBobNostrConnection = observer(({ bob }: { bob: Bob }) => {
  const connection = bob.selectedNostrConnection;
  const [twoFACode, setTwoFACode] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showRemoveWarning, setShowRemoveWarning] = useState(false);

  useEffect(() => {
    console.log("in EditBobNostrConnection useffect");
    bob.checkIfWeNeedToShowTwoFactorForm();
  }, []);

  if (!connection) return null;

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    connection.setName(e.currentTarget.value);
    setHasChanges(true);
  };

  const handleAddBudget = () => {
    connection.setBudget(100000, 30, 1096);
    handleSaveConnection();
    setHasChanges(true);
  };

  const handleConfirmRemoveBudget = () => {
    connection.setBudget(null, null, null);
    handleSaveConnection();
    setShowRemoveWarning(false);
    setHasChanges(true);
  };

  const handleBudgetAmountChange = (value: string) => {
    connection.setBudget(
      Number(value),
      connection.budget_renewal_period_days,
      connection.connection_expiration_days
    );
    setHasChanges(true);
  };

  const handleRenewalPeriodChange = (value: string) => {
    connection.setBudget(
      connection.budget_amount_sats,
      Number(value),
      connection.connection_expiration_days
    );
    setHasChanges(true);
  };

  const handleExpirationChange = (value: string) => {
    connection.setBudget(
      connection.budget_amount_sats,
      connection.budget_renewal_period_days,
      Number(value)
    );
    setHasChanges(true);
  };

  const handleSaveConnection = () => {
    bob.updateNostrConnection({
      nostrConnectionUuid: connection.uuid,
      name: connection.name,
      budgetAmountSats: connection.budget_amount_sats,
      budgetRenewalPeriodDays: connection.budget_renewal_period_days,
      connectionExpirationDays: connection.connection_expiration_days,
      twoFactorCode: twoFACode.trim() || null,
    });
    connection.setEditorialViewStatus(null);
    bob.setOpenModal(BOB_MODALS.NWC_SECURE);
  };

  const twoFAVisible =
    bob.workspace.accountRequiresTwoFactorFormForActions && hasChanges;

  return (
    <Stack gap="md">
      <TextInput
        label="Name"
        value={connection.name}
        onChange={handleNameChange}
        disabled={isLoading}
      />

      {connection.budget_amount_sats === null ? (
        <>
          <Alert
            icon={<IconAlertTriangle size={16} />}
            title="Budget Recommended"
            color="red"
          >
            An app could quickly spend all your sats. Set a budget for
            protection.
          </Alert>
          <Button onClick={handleAddBudget} disabled={isLoading}>
            Add Budget
          </Button>
        </>
      ) : (
        <>
          {/* <Text fw={500}>Spend no more than...</Text>
          <SegmentedControl
            value={String(connection.budget_amount_sats)}
            onChange={handleBudgetAmountChange}
            orientation="vertical"
            data={[
              { label: "10k sats", value: "10000" },
              { label: "100k sats", value: "100000" },
              { label: "250k sats", value: "250000" },
              { label: "1M sats", value: "1000000" },
              { label: "5M sats", value: "5000000" },
              { label: "50M sats", value: "50000000" },
            ]}
            disabled={isLoading}
          /> */}
          <Text fw={500}>Spend no more than...</Text>
          <Select
            value={
              connection.budget_amount_sats === null
                ? null
                : String(connection.budget_amount_sats)
            }
            onChange={(val) => val && handleBudgetAmountChange(val)}
            data={[
              { value: "10000", label: "10,000 satoshis" },
              { value: "50000", label: "50,000 satoshis" },
              { value: "100000", label: "100,000 satoshis" },
              { value: "250000", label: "250,000 satoshis" },
              { value: "500000", label: "500,000 satoshis" },
              { value: "1000000", label: "1 million satoshis" },
              { value: "5000000", label: "5 million satoshis" },
              { value: "50000000", label: "50 million satoshis" },
            ]}
            disabled={isLoading}
            placeholder="Select amount"
          />

          <Text fw={500}>Every...</Text>
          <SegmentedControl
            value={String(connection.budget_renewal_period_days)}
            onChange={handleRenewalPeriodChange}
            data={[
              { label: "30 days", value: "30" },
              { label: "1 year", value: "365" },
            ]}
            disabled={isLoading}
          />

          <Text fw={500}>Expire connection in...</Text>
          <SegmentedControl
            value={String(connection.connection_expiration_days)}
            onChange={handleExpirationChange}
            data={[
              { label: "90 days", value: "90" },
              { label: "1 year", value: "365" },
              { label: "3 years", value: "1096" },
            ]}
            disabled={isLoading}
          />

          <Button
            color="red"
            variant="outline"
            leftSection={<IconTrash size={16} />}
            onClick={() => setShowRemoveWarning(true)}
            disabled={isLoading}
          >
            Remove Budget & Expiration
          </Button>
        </>
      )}

      {showRemoveWarning && (
        <Stack gap="sm">
          <Alert
            icon={<IconAlertTriangle size={16} />}
            title="Warning"
            color="red"
            withCloseButton
            onClose={() => setShowRemoveWarning(false)}
          >
            Removing the budget is potentially dangerous and could allow an app
            to drain your outbound funds.
          </Alert>
          <Button
            color="red"
            variant="filled"
            leftSection={<IconTrash size={16} />}
            onClick={handleConfirmRemoveBudget}
            mt="sm"
          >
            Remove Budget
          </Button>
        </Stack>
      )}

      {isLoading ? (
        <Loader />
      ) : (
        <Stack>
          <TwoFactorInput
            visible={twoFAVisible}
            code={twoFACode}
            onCodeChange={setTwoFACode}
          />

          <Group justify="flex-end" mt="md">
            <Button
              variant="outline"
              onClick={() => {
                connection.setEditorialViewStatus(null);
                bob.setOpenModal(null);
              }}
            >
              Exit
            </Button>
            <Button onClick={handleSaveConnection} disabled={!hasChanges}>
              Save Connection
            </Button>
          </Group>
        </Stack>
      )}
    </Stack>
  );
});

export default EditBobNostrConnection;
